import axios from 'axios';
import { API_BASE_URL, NODE_API_URL, SHOPIFY_API_KEY } from '../config/env';
import { getSessionToken } from '@shopify/app-bridge-utils';
import createApp from '@shopify/app-bridge';

/**
 * Get or create App Bridge instance
 * @param {Object} app - Optional App Bridge instance passed directly
 * @returns {Object|null} App Bridge instance or null
 */
const getAppBridgeInstance = (app = null) => {
    // If app is provided directly, use it
    if (app) {
        return app;
    }
    
    // Check if window is available (for SSR compatibility)
    if (typeof window === 'undefined') {
        return null;
    }
    
    // Try to get from global reference (set by AppBridgeProvider)
    if (window.__shopifyAppBridgeInstance) {
        return window.__shopifyAppBridgeInstance;
    }
    
    // If not available, try to create it on-demand using URL params
    // This ensures we always have an app instance when needed
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const host = urlParams.get('host');
        const apiKey = SHOPIFY_API_KEY;
        
        if (apiKey && host) {
            // Create App Bridge instance on-demand
            const appInstance = createApp({
                apiKey: apiKey,
                host: host,
                forceRedirect: process.env.NODE_ENV === 'production',
            });
            
            // Cache it globally for future use
            window.__shopifyAppBridgeInstance = appInstance;
            
            return appInstance;
        }
    } catch (error) {
        console.error('Failed to create App Bridge instance:', error);
    }
    
    return null;
};

// Single command function to call API with any method
/**
 * Make an authenticated API call to the backend
 * @param {string} endpoint - API endpoint (relative to API_BASE_URL)
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE, etc.)
 * @param {Object|null} data - Request body data (for POST, PUT, etc.)
 * @param {Object|null} app - Optional App Bridge instance (if not provided, will try to get from global)
 * @returns {Promise<Object>} Response data from the API
 */
const apiCall = async (endpoint, method = 'GET', data = null, app = null) => {
    try {
        const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
        // Redux dispatches checkInstall before AppBridgeProvider mounts. getSessionToken()
        // can hang until App Bridge is ready, so POST /install never starts. Install only needs { shop }.
        const isInstallEndpoint = cleanEndpoint === 'install';

        const appBridge = isInstallEndpoint ? null : getAppBridgeInstance(app);

        let sessionToken = null;
        if (appBridge) {
            try {
                sessionToken = await getSessionToken(appBridge);
            } catch (tokenError) {
                console.error('Failed to get session token:', tokenError);
            }
        } else if (!isInstallEndpoint) {
            console.warn('App Bridge instance not available. API call may fail authentication.');
        }

        let cleanBaseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
        cleanBaseUrl = isInstallEndpoint ? NODE_API_URL : cleanBaseUrl;
        const fullUrl = `${cleanBaseUrl}/${cleanEndpoint}`;
        // Build headers
        const headers = {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': 'true',
        };
        
        // Add authorization header only if session token is available
        if (sessionToken) {
            headers['authorization'] = `Bearer ${sessionToken}`;
        }
        
        const response = await axios({
            method: method.toUpperCase(),
            url: fullUrl,
            data: data,
            headers: headers,
            timeout: 10000, // 10 second timeout
        });

        return response.data;
    } catch (error) {
        console.error('API call failed:', {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            message: error.message
        });

        // If we get HTML response, it means the route wasn't found
        if (error.response?.data && typeof error.response.data === 'string' && error.response.data.includes('<!DOCTYPE')) {
            throw new Error(`API endpoint not found. Check if backend server is running and route is correct: ${error.config?.url}`);
        }

        throw error.response?.data || error.message;
    }
};

async function graphqlCall(query) {
    // Check if App Bridge is available (window.shopify exists when app is embedded)
    if (window.shopify) {
        try {
            let grphData = await fetch('shopify:admin/api/graphql.json', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(query)
            });
            grphData = await grphData.json();
            return grphData;
        } catch (error) {
            console.error('GraphQL call error:', error);
            return ({
                data: {},
                errors: [{ message: error.message || 'GraphQL request failed' }]
            });
        }
    }
    return ({ data: false, errors: [{ message: 'App Bridge is not available' }] });
}

/**
 * Decode HTML entities in a string
 * @param {string} html - HTML encoded string
 * @returns {string} Decoded string
 */
function decodeHtmlEntities(html) {
  if (typeof html !== 'string') {
    return html;
  }
  
  // Create a temporary textarea element to decode HTML entities
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
}

/**
 * Encode HTML entities in a string
 * @param {string} text - Plain text string
 * @returns {string} HTML encoded string
 */
function encodeHtmlEntities(text) {
  if (typeof text !== 'string') {
    return text;
  }
  
  // Create a temporary textarea element to encode HTML entities
  const textarea = document.createElement('textarea');
  textarea.textContent = text;
  return textarea.innerHTML;
}

/**
 * Recursively decode HTML entities in an object/array
 * @param {any} obj - Object or value to decode
 * @returns {any} Decoded object or value
 */
function decodeHtmlEntitiesDeep(obj) {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (typeof obj === 'string') {
    return decodeHtmlEntities(obj);
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => decodeHtmlEntitiesDeep(item));
  }
  
  if (typeof obj === 'object') {
    const decoded = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        decoded[key] = decodeHtmlEntitiesDeep(obj[key]);
      }
    }
    return decoded;
  }
  
  return obj;
}

/**
 * Recursively encode HTML entities in an object/array
 * @param {any} obj - Object or value to encode
 * @returns {any} Encoded object or value
 */
function encodeHtmlEntitiesDeep(obj) {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (typeof obj === 'string') {
    return encodeHtmlEntities(obj);
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => encodeHtmlEntitiesDeep(item));
  }
  
  if (typeof obj === 'object') {
    const encoded = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        encoded[key] = encodeHtmlEntitiesDeep(obj[key]);
      }
    }
    return encoded;
  }
  
  return obj;
}

/**
 * Format money amount using the money_format template from Shopify
 * @param {number|string} amount - The amount to format
 * @param {string} moneyFormat - The money format template (e.g., "${{amount}}", "₹{{amount}}", "{{amount}} USD")
 * @param {object} options - Formatting options
 * @param {number} options.decimals - Number of decimal places (default: 2)
 * @returns {string} Formatted money string
 */
function formatMoney(amount, moneyFormat = '${{amount}}', options = {}) {
  // Handle null, undefined, or empty values
  if (amount === null || amount === undefined || amount === '') {
    amount = 0;
  }
  
  // Convert to number
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Handle NaN
  if (isNaN(numAmount)) {
    return moneyFormat.replace('{{amount}}', '0.00');
  }
  
  // Default options
  const { decimals = 2 } = options;
  
  // Format the number with thousand separators and decimal places
  const formattedAmount = numAmount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  
  // Replace {{amount}} placeholder in the money format
  return moneyFormat.replace('{{amount}}', formattedAmount);
}

export { apiCall, graphqlCall, decodeHtmlEntities, encodeHtmlEntities, decodeHtmlEntitiesDeep, encodeHtmlEntitiesDeep, formatMoney };

